import "./index.css"
import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { formatMomentDate } from "../../utils/dateHelper";
import { 
  Globe, 
  Users, 
  Lock, 
  Clock, 
  ChevronLeft, 
  ChevronRight,
  Activity
} from "lucide-react";
import { LikeIcon, CommentIcon } from "../../assets/icons";
import withModal from "../withModal";
import { getUserInitial } from "../../utils/otherHelopers";

const PostView = ({ post, author, onLikesClick, onCommentsClick }) => {
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);

  useEffect(() => {
    setActiveMediaIndex(0);
  }, [post]);

  if (!post) return null;

  const media = post.media || [];
  const hasMedia = media.length > 0;
  
  const handlePrevMedia = (e) => {
    e.stopPropagation();
    setActiveMediaIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const handleNextMedia = (e) => {
    e.stopPropagation();
    setActiveMediaIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  const getVisibilityIcon = (visibility) => {
    switch (visibility?.toLowerCase()) {
      case "public":
        return <Globe size={14} className="me-1" />;
      case "connections":
      case "friends":
        return <Users size={14} className="me-1" />;
      case "private":
      case "only_me":
        return <Lock size={14} className="me-1" />;
      default:
        return <Globe size={14} className="me-1" />;
    }
  };

  // Author details (support both User and Company structures)
  const isCompanyData = (data) => {
    if (!data) return false;
    if (data.first_name || data.last_name) return false;
    if (data.name) return true;
    return false;
  };

  const isCompany = post?.author_type 
    ? post.author_type === 'company'
    : author 
      ? isCompanyData(author) 
      : (isCompanyData(post?.user) || !!post?.company);

  const resolveAuthorName = (data) => {
    if (!data) return null;
    if (data.first_name || data.last_name) return `${data.first_name || ""} ${data.last_name || ""}`.trim(); // User
    if (data.name) return data.name; // Company
    return null;
  };

  const authorName = resolveAuthorName(author) || resolveAuthorName(post?.user) || resolveAuthorName(post?.company) || "User";

  const authorAvatar = isCompany
    ? (author?.profile?.logo?.url || post?.company?.profile?.logo?.url || post?.user?.profile?.logo?.url)
    : (author?.profile?.profile_photo?.url || post?.user?.profile?.profile_photo?.url);

  const authorProfession = isCompany
    ? (author?.category?.name || post?.company?.category?.name || post?.user?.category?.name || null)
    : (author?.profile?.profession?.name || post?.user?.profile?.profession?.name || "Member");

  const likesCount = post.reactions?.total || post.likeCount || 0;
  const commentsCount = post.commentCount || 0;

  const currentMedia = hasMedia ? media[activeMediaIndex] : null;
  const currentMediaUrl = currentMedia ? (currentMedia.file?.url || currentMedia.url) : null;
  const isVideo = currentMedia?.mimetype?.startsWith("video/") || false;

  return (
    <>
     

      <Modal.Body className="post-view-layout">
        {/* Style block for premium localized CSS */}
        

       
          {/* Media Column (only rendered if media exists) */}
          {hasMedia && (
            <div className="post-view-media-col">
              <div className="post-view-media-container">
                {isVideo ? (
                  <video
                    src={currentMediaUrl}
                    controls
                    className="post-view-media-item"
                  />
                ) : (
                  <img
                    src={currentMediaUrl}
                    alt="Post Media"
                    className="post-view-media-item"
                    onError={(e) => {
                      e.target.src = "/images/image-placeholder.png";
                    }}
                  />
                )}
              </div>

              {/* Navigation arrows (if > 1 media item) */}
              {media.length > 1 && (
                <>
                  <button
                    onClick={handlePrevMedia}
                    className="post-view-nav-arrow prev"
                    aria-label="Previous media"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={handleNextMedia}
                    className="post-view-nav-arrow next"
                    aria-label="Next media"
                  >
                    <ChevronRight size={20} />
                  </button>

                  {/* Indicator dots */}
                  <div className="post-view-indicator-bar">
                    {media.map((_, idx) => (
                      <div
                        key={idx}
                        className={`post-view-indicator-dot ${
                          activeMediaIndex === idx ? "active" : ""
                        }`}
                        onClick={() => setActiveMediaIndex(idx)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Details Column */}
          <div className="post-view-details-col">
            {/* Header */}
            <div className="post-view-header">
              {authorAvatar ? (
                <img
                  src={authorAvatar}
                  alt={authorName}
                  className="post-view-avatar"
                />
              ) : (
                <div className="post-view-avatar-placeholder">
                  {getUserInitial(authorName)}
                </div>
              )}
              <div className="post-view-meta-info">
                <h6 className="post-view-author-name">{authorName}</h6>
                {authorProfession && (
                  <div className="post-view-author-profession">{authorProfession}</div>
                )}
                <div className="post-view-time">
                  <Clock size={12} />
                  <span>
                    {formatMomentDate(post.created_at || post.createdAt, "DD MMM YYYY, hh:mm A")}
                  </span>
                </div>
              </div>
            </div>

            {/* Badges (Visibility & Status) */}
            <div className="post-view-badge-row">
              <span className="post-view-chip post-view-chip-visibility">
                {getVisibilityIcon(post.visibility)}
                {post.visibility || "Public"}
              </span>

              <span
                className={`post-view-chip ${
                  post.status === "active"
                    ? "post-view-chip-status-active"
                    : "post-view-chip-status-inactive"
                }`}
              >
                <Activity size={12} className="me-1" />
                {post.status || "Active"}
              </span>
            </div>

            {/* Content text */}
            <div 
              className="post-view-body-content"
              dangerouslySetInnerHTML={{ __html: post.content || "-" }}
            />

            {/* Footer with metrics */}
            <div className="post-view-footer">
              <div className="post-view-engagement">
                <button
                  className={`post-view-metric ${likesCount > 0 ? "clickable" : ""}`}
                  onClick={() => likesCount > 0 && onLikesClick && onLikesClick(post.id)}
                  title={likesCount > 0 ? "View Likes" : ""}
                  disabled={likesCount === 0}
                >
                  <LikeIcon size={16} />
                  <span>{likesCount} Likes</span>
                </button>

                <button
                  className={`post-view-metric ${commentsCount > 0 ? "clickable" : ""}`}
                  onClick={() => commentsCount > 0 && onCommentsClick && onCommentsClick(post.id)}
                  title={commentsCount > 0 ? "View Comments" : ""}
                  disabled={commentsCount === 0}
                >
                  <CommentIcon size={16} />
                  <span>{commentsCount} Comments</span>
                </button>
              </div>
            </div>
          </div>
        
      </Modal.Body>
    </>
  );
};

export default withModal(PostView);
